import {
  makeStyles,
  shorthands,
  Spinner,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  getProjectIqCapabilities,
  getProjectIqNotifications,
  markProjectIqNotificationSeen,
  getProjectIqWorkspaces,
  pinWorkspace,
  unpinWorkspace,
  getSowAnalysis,
} from "apps/project-iq/api";
import { EditProjectIqWorkspaceDialog } from "apps/project-iq/components/EditProjectIqWorkspaceDialog/EditProjectIqWorkspaceDialog";
import { WorkspaceEmptyState } from "apps/project-iq/components/WorkspaceEmptyState/WorkspaceEmptyState";
import { WorkspaceNav } from "apps/project-iq/components/WorkspaceNav/WorkspaceNav";
import { WorkspacePageHeader } from "apps/project-iq/components/WorkspacePageHeader/WorkspacePageHeader";
import {
  PlanSaveStatus,
  PlanTab,
} from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";
import { PeopleSaveStatus } from "apps/project-iq/components/WorkspacePeople/WorkspacePeople.types";
import { ProjectReviewSaveStatus } from "apps/project-iq/components/ProjectReview/ProjectReview.types";
import { DocumentsSaveStatus } from "apps/project-iq/components/WorkspaceDocuments/WorkspaceDocuments.types";
import { ValueSaveStatus } from "apps/project-iq/pages/ValuePage/ValuePage.types";
import { WorkspaceSidebar } from "apps/project-iq/components/WorkspaceSidebar/WorkspaceSidebar";
import {
  ITabSharedProps,
  WORKSPACE_TAB_REGISTRY,
} from "apps/project-iq/constants/workspaceTabRegistry";
import { WORKSPACE_TAB_TITLES } from "apps/project-iq/constants/workspaceTabs";
import { DiagnosticDetailPage } from "apps/project-iq/pages/DiagnosticDetailPage/DiagnosticDetailPage";
import { AnalyticsDetailPage } from "apps/project-iq/pages/AnalyticsDetailPage/AnalyticsDetailPage";
import { ProjectIqWorkspacesPage } from "apps/project-iq/pages/ProjectIqWorkspacesPage";
import { PortfoliosDashboardPage } from "apps/project-iq/pages/PortfoliosDashboardPage";
import { PracticesDashboardPage } from "apps/project-iq/pages/PracticesDashboardPage";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  selectAvailableProjectIqWorkspaces,
  selectIsProjectIqWorkspacesLoading,
  refreshProjectIqWorkspaces,
  setProjectIqWorkspaces,
  toggleWorkspacePin,
} from "redux/projectIqWorkspacesSlice";
import { showToast } from "redux/toastActions";
import { subscribeToProjectIqActivity } from "utils/projectIqActivity";
import { AppDispatch, RootState } from "store";
import { designTokens } from "theme/customTheme";
import { AnnouncementsBanner } from "components/manageAnnouncements/announcementsBanner/AnnouncementsBanner";

const WORKSPACES_PATH = "/workspaces";
const LEADERSHIP_PORTFOLIOS_PATH = "/leadership/portfolios";
const LEADERSHIP_PRACTICES_PATH = "/leadership/practices";
const SOW_ANALYSIS_TOAST_POLL_INTERVAL_MS = 3000;
const WORKSPACE_REFRESH_DEBOUNCE_MS = 500;
const SOW_ANALYSIS_TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "skipped_not_empty",
  "skipped_no_content",
]);

const useStyles = makeStyles({
  root: {
    display: "flex",
    height: "100vh",
    width: "100%",
    overflow: "hidden",
    backgroundColor: designTokens.neutral[50],
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
    minHeight: 0,
  },
  workspaceNav: {
    width: "260px",
    minWidth: "260px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: projectIqTokens.workspaceNavBg,
    borderRight: `1px solid ${projectIqTokens.line}`,
    height: "100%",
  },
  workspaceLoadingContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tabContent: {},
  tabContentFull: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
    color: designTokens.neutral[500],
  },
  planSaveIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.faint,
  },
  planSaveError: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: tokens.colorPaletteRedForeground1,
  },
  connectSharePointButton: {
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: projectIqTokens.teal,
    color: designTokens.neutral.white,
    fontWeight: designTokens.fontWeight.bold,
    fontSize: designTokens.fontSize.sm,
    fontFamily: projectIqTokens.fontFamily,
    ...shorthands.padding(designTokens.spacing[2]),
    ":hover": {
      backgroundColor: projectIqTokens.tealInk,
      color: designTokens.neutral.white,
    },
    ":active": {
      backgroundColor: projectIqTokens.tealInk,
      color: designTokens.neutral.white,
    },
  },
});

const markNotificationSeen = async (id: string): Promise<void> => {
  await markProjectIqNotificationSeen(id);
};

export const ProjectIqApp = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewAll = location.pathname === WORKSPACES_PATH;
  const isLeadershipPortfolios =
    location.pathname === LEADERSHIP_PORTFOLIOS_PATH;
  const isLeadershipPractices = location.pathname === LEADERSHIP_PRACTICES_PATH;
  const isLeadershipDashboard = isLeadershipPortfolios || isLeadershipPractices;
  const {
    workspaceId: urlWorkspaceId,
    diagnosticId: urlDiagnosticId,
    analyticsId: urlAnalyticsId,
    tab: urlTab,
  } = useParams<{
    workspaceId: string;
    diagnosticId?: string;
    analyticsId?: string;
    tab?: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const isWorkspacesLoading = useSelector(selectIsProjectIqWorkspacesLoading);
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const pinnedWorkspaceIds = workspaces
    .filter((w) => w.isPinned)
    .map((w) => w.id);

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  );
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [showEditCapabilitiesDialog, setShowEditCapabilitiesDialog] =
    useState<boolean>(false);
  const { data: availableCapabilities = [], isLoading: isCapabilitiesLoading } =
    useQuery("projectIqCapabilities", getProjectIqCapabilities, {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    });

  const [isCapabilityCreateOpen, setIsCapabilityCreateOpen] =
    useState<boolean>(false);
  const [planSaveStatus, setPlanSaveStatus] = useState<PlanSaveStatus>("idle");
  const [planInitialTab, setPlanInitialTab] = useState<PlanTab | undefined>(
    undefined,
  );
  const [peopleSaveStatus, setPeopleSaveStatus] =
    useState<PeopleSaveStatus>("idle");
  const [projectReviewSaveStatus, setProjectReviewSaveStatus] =
    useState<ProjectReviewSaveStatus>("idle");
  const [documentsSaveStatus, setDocumentsSaveStatus] =
    useState<DocumentsSaveStatus>("idle");
  const [documentsSaveStatusLabel, setDocumentsSaveStatusLabel] = useState<
    string | undefined
  >(undefined);
  const [valueSaveStatus, setValueSaveStatus] =
    useState<ValueSaveStatus>("idle");
  const [chatConversationOpen, setChatConversationOpen] =
    useState<boolean>(false);

  useLayoutEffect(() => {
    document.title = "Project IQ";
  }, []);

  useEffect(() => {
    getProjectIqWorkspaces().then((result) => {
      dispatch(setProjectIqWorkspaces(result));
    });
  }, [dispatch]);

  useEffect(() => {
    let refreshTimeout: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = subscribeToProjectIqActivity(() => {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        getProjectIqWorkspaces()
          .then((result) => dispatch(refreshProjectIqWorkspaces(result)))
          .catch(() => undefined);
      }, WORKSPACE_REFRESH_DEBOUNCE_MS);
    });
    return () => {
      unsubscribe();
      clearTimeout(refreshTimeout);
    };
  }, [dispatch]);

  useEffect(() => {
    setSelectedWorkspaceId(urlWorkspaceId ?? null);
  }, [urlWorkspaceId]);

  const activeTab = urlDiagnosticId
    ? "nh-diagnostic-iq"
    : urlAnalyticsId
      ? "ai-analytics"
      : urlTab && WORKSPACE_TAB_TITLES[urlTab]
        ? urlTab
        : "overview";

  useEffect(() => {
    if (activeTab !== "plan") {
      setPlanSaveStatus("idle");
      setPlanInitialTab(undefined);
    }
    if (activeTab !== "people") {
      setPeopleSaveStatus("idle");
    }
    if (activeTab !== "project-reviews") {
      setProjectReviewSaveStatus("idle");
    }
    if (activeTab !== "value") {
      setValueSaveStatus("idle");
    }
    if (activeTab !== "chat") {
      setChatConversationOpen(false);
    }
  }, [activeTab]);

  const selectedWorkspace =
    workspaces.find((w) => w.id === selectedWorkspaceId) ?? null;
  const isUrlWorkspaceUnavailable =
    urlWorkspaceId != null &&
    !isWorkspacesLoading &&
    !workspaces.some((w) => w.id === urlWorkspaceId);

  const previousSowAnalysisStatusRef = useRef<
    Record<string, string | undefined>
  >({});

  const notifySowAnalysisStatus = useCallback(
    (workspaceId: string, status: string) => {
      const previousStatus = previousSowAnalysisStatusRef.current[workspaceId];
      previousSowAnalysisStatusRef.current[workspaceId] = status;
      if (previousStatus === status) return;

      if (status === "processing") {
        dispatch(showToast("SOW analysis started…", "info"));
        return;
      }
      if (previousStatus !== "processing") return;

      if (status === "completed") {
        dispatch(
          showToast(
            "SOW analysis completed. New items have been added to your plan.",
            "success",
          ),
        );
      } else if (status === "failed") {
        dispatch(showToast("SOW analysis failed.", "error"));
      } else if (status === "skipped_not_empty") {
        dispatch(
          showToast(
            "SOW analysis finished: your plan already had content, so nothing was changed.",
            "warning",
          ),
        );
      } else if (status === "skipped_no_content") {
        dispatch(
          showToast(
            "SOW analysis finished: no extractable content was found in the document.",
            "warning",
          ),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const workspaceId = selectedWorkspace?.id;
    const status = selectedWorkspace?.sowAnalysis?.status;
    if (!workspaceId || !status) return;

    notifySowAnalysisStatus(workspaceId, status);
    if (status !== "processing") return;

    const interval = setInterval(async () => {
      try {
        const latest = await getSowAnalysis({ workspaceId });
        notifySowAnalysisStatus(workspaceId, latest.status);
        if (SOW_ANALYSIS_TERMINAL_STATUSES.has(latest.status)) {
          const result = await getProjectIqWorkspaces();
          dispatch(setProjectIqWorkspaces(result));
        }
      } catch {
        // Silently retry on the next tick.
      }
    }, SOW_ANALYSIS_TOAST_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [
    selectedWorkspace?.id,
    selectedWorkspace?.sowAnalysis?.status,
    notifySowAnalysisStatus,
    dispatch,
  ]);

  const handleNavigateToTab = useCallback(
    (tab: string, subTab?: string) => {
      if (tab === "plan") {
        setPlanInitialTab(subTab ? (subTab as PlanTab) : undefined);
      }
      if (selectedWorkspace) {
        navigate(`/workspace/${selectedWorkspace.id}/${tab}`);
      }
    },
    [selectedWorkspace, navigate],
  );

  const handleTogglePin = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const workspace = workspaces.find((w) => w.id === id);
    if (!workspace) return;
    const wasPinned = workspace.isPinned;
    dispatch(toggleWorkspacePin(id));
    try {
      if (wasPinned) {
        await unpinWorkspace({ workspaceId: id });
      } else {
        await pinWorkspace({ workspaceId: id });
      }
    } catch {
      dispatch(toggleWorkspacePin(id));
    }
  };

  const handleSelectWorkspace = (id: string) => {
    setSelectedWorkspaceId(id);
    navigate(`/workspace/${id}`);
  };

  const handleCreateDialogDismiss = async (
    isSuccess: boolean,
    createdWorkspace?: { id: string },
  ) => {
    setShowCreateDialog(false);
    if (isSuccess && createdWorkspace?.id) {
      handleSelectWorkspace(createdWorkspace.id);
    }
    const updated = await getProjectIqWorkspaces();
    if (!updated) return;
    dispatch(setProjectIqWorkspaces(updated));
  };

  const tabSharedProps: ITabSharedProps = {
    workspaceId: selectedWorkspace?.id ?? "",
    workspaceName: selectedWorkspace?.name ?? "",
    workspaceCode: selectedWorkspace?.code,
    clientName: selectedWorkspace?.clientName ?? "",
    workspace: selectedWorkspace!,
    planSaveStatus,
    onSaveStatusChange: setPlanSaveStatus,
    planInitialTab,
    planSaveIndicatorClass: styles.planSaveIndicator,
    planSaveErrorClass: styles.planSaveError,
    peopleSaveStatus,
    onPeopleSaveStatusChange: setPeopleSaveStatus,
    projectReviewSaveStatus,
    onProjectReviewSaveStatusChange: setProjectReviewSaveStatus,
    documentsSaveStatus,
    documentsSaveStatusLabel,
    onDocumentsSaveStatusChange: (status, label) => {
      setDocumentsSaveStatus(status);
      setDocumentsSaveStatusLabel(label);
    },
    onNavigateToTab: handleNavigateToTab,
    availableCapabilities,
    isCapabilitiesLoading,
    isCapabilityCreateOpen,
    onCapabilityCreateOpen: () => setIsCapabilityCreateOpen(true),
    onCapabilityCreateClose: () => setIsCapabilityCreateOpen(false),
    onClientViewClick: () => {},
    chatConversationOpen,
    onChatConversationOpenChange: setChatConversationOpen,
  };

  return (
    <div className={styles.root}>
      <WorkspaceSidebar
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        isWorkspacesLoading={isWorkspacesLoading}
        currentUser={currentUser}
        pinnedWorkspaceIds={pinnedWorkspaceIds}
        onSelectWorkspace={handleSelectWorkspace}
        onAddWorkspace={() => setShowCreateDialog(true)}
        onViewAllWorkspaces={() => navigate("/workspaces")}
        onTogglePin={handleTogglePin}
      />

      {!isViewAll && !isLeadershipDashboard && selectedWorkspace != null && (
        <aside className={styles.workspaceNav}>
          <WorkspaceNav
            workspace={selectedWorkspace}
            activeTab={activeTab}
            onTabSelect={(tab) =>
              navigate(`/workspace/${selectedWorkspace.id}/${tab}`)
            }
            onAddCapability={() => setShowEditCapabilitiesDialog(true)}
          />
        </aside>
      )}

      <main className={styles.main}>
        <AnnouncementsBanner
          fetchFn={getProjectIqNotifications}
          markSeenFn={markNotificationSeen}
        />
        {isWorkspacesLoading && urlWorkspaceId ? (
          <div className={styles.workspaceLoadingContainer}>
            <Spinner label="Loading workspace..." />
          </div>
        ) : urlDiagnosticId ? (
          <DiagnosticDetailPage />
        ) : urlAnalyticsId ? (
          <AnalyticsDetailPage
            workspaceId={urlWorkspaceId!}
            analyticsId={urlAnalyticsId}
          />
        ) : isViewAll ? (
          <ProjectIqWorkspacesPage
            pinnedIdentifiers={pinnedWorkspaceIds}
            onTogglePin={handleTogglePin}
          />
        ) : isLeadershipPortfolios ? (
          <PortfoliosDashboardPage />
        ) : isLeadershipPractices ? (
          <PracticesDashboardPage />
        ) : selectedWorkspace == null ? (
          <WorkspaceEmptyState
            onCreateWorkspace={() => setShowCreateDialog(true)}
            isWorkspaceUnavailable={isUrlWorkspaceUnavailable}
          />
        ) : (
          <>
            {!(activeTab === "chat" && chatConversationOpen) && (
              <WorkspacePageHeader
                title={WORKSPACE_TAB_TITLES[activeTab]}
                action={WORKSPACE_TAB_REGISTRY[activeTab]?.headerAction?.(
                  tabSharedProps,
                )}
              />
            )}
            <div
              className={
                WORKSPACE_TAB_REGISTRY[activeTab]?.isFullContent
                  ? styles.tabContentFull
                  : styles.tabContent
              }
            >
              {WORKSPACE_TAB_REGISTRY[activeTab]?.render(tabSharedProps) ?? (
                <Text>Coming soon</Text>
              )}
            </div>
          </>
        )}
      </main>

      {showCreateDialog && (
        <EditProjectIqWorkspaceDialog
          isOpen={showCreateDialog}
          workspace={null}
          onDismiss={handleCreateDialogDismiss}
        />
      )}

      {showEditCapabilitiesDialog && selectedWorkspace != null && (
        <EditProjectIqWorkspaceDialog
          isOpen={showEditCapabilitiesDialog}
          workspace={selectedWorkspace}
          initialTab="settings"
          onDismiss={async (success) => {
            setShowEditCapabilitiesDialog(false);
            if (success) {
              const updated = await getProjectIqWorkspaces();
              dispatch(setProjectIqWorkspaces(updated));
            }
          }}
        />
      )}
    </div>
  );
};
